import React, { useState, useEffect } from 'react'
import { TaskService } from '@duck/api-definitions'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@duck/ui'
import { apis } from '@modules/apiClients'
import { useTaskValidation } from '@/hooks/useTaskValidation'
import { AxiosError } from 'axios'
import { useTar } from '@/modules'
import { DetailsConfigSection } from './DetailsConfigSection'
import { DataSection } from './DataSection'
import { PayloadSection } from './PayloadSection'
import { BodySection } from './BodySection'
import { ActionsSection } from './ActionsSection'

interface TaskDialogProps {
  isOpen: boolean
  onClose: () => void
  task: TaskService.Task | null
  onTaskSaved?: () => void
  allAccessGroups: string[]
  isLoading?: boolean
}


export const TaskDialog: React.FC<TaskDialogProps> = ({ isOpen, onClose, task, onTaskSaved, allAccessGroups, isLoading = false }) => {
  const [formData, setFormData] = useState<Partial<TaskService.TaskBase>>({ allowedWeapons: ['SQL'] })
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const { validateTask, clearErrors, fieldErrors } = useTaskValidation()
  const { t } = useTar()

  // Reset form data when task changes
  useEffect(() => {
    if (task) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { id: _, ...taskData } = task
      setFormData({ ...taskData, allowedWeapons: taskData.allowedWeapons ?? ['SQL'] })
    } else {
      setFormData({ allowedWeapons: ['SQL'] })
    }
    setSaveError(null)
    setIsSaving(false)
    clearErrors()
  }, [task, clearErrors])

  const handleInputChange = (
    field: keyof TaskService.Task,
    value: string | number | string[] | TaskService.TaskWeaponsOfChoiceEnum[] | undefined,
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleSave = async () => {
    try {
      setIsSaving(true)
      setSaveError(null)

      const isNewTask = !task
      const validation = validateTask(formData, isNewTask)

      if (!validation.isValid) {
        const errorMessages = Object.entries(validation.errors)
          .map(([field, msg]) => `${field}: ${msg}`)
          .join('; ')
        setSaveError(`Validation failed: ${errorMessages}`)
        return
      }

      if (task) {
        await apis.taskService.idPut(task.id, formData as TaskService.TaskBase)
      } else {
        await apis.taskService.rootPost(formData as TaskService.TaskBase)
      }

      onTaskSaved?.()
      onClose()
    } catch (error) {
      console.error('Failed to save task:', error)
      setSaveError(error instanceof AxiosError ? error?.response?.data?.error : 'Failed to save task. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!task) return

    try {
      setIsSaving(true)
      setSaveError(null)

      await apis.taskService.idDelete(task.id)

      onTaskSaved?.()
      onClose()
    } catch (error) {
      console.error('Failed to delete task:', error)
      setSaveError(error instanceof AxiosError ? error?.response?.data?.error : 'Failed to delete task. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }


  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => !isSaving && !open && onClose()}
    >
      <DialogContent
        className="sm:max-w-5xl lg:max-w-6xl xl:max-w-7xl sm:min-w-[600px] lg:min-w-[800px] max-h-[90vh] overflow-y-auto"
        onPointerDownOutside={(e) => isSaving && e.preventDefault()}
        onEscapeKeyDown={(e) => isSaving && e.preventDefault()}
        onInteractOutside={(e) => isSaving && e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-[28px] italic font-medium text-primary/80">
            {task ? task.name : t('tasks.newTask')}
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
          </div>
        ) : (
        <Tabs defaultValue="details" className="w-full">
          <TabsList className={`grid w-full ${task ? 'grid-cols-5' : 'grid-cols-4'}`}>
            <TabsTrigger value="details">
              {t('common.details')}
            </TabsTrigger>
            <TabsTrigger value="data">
              {t('common.data')}
            </TabsTrigger>
            <TabsTrigger value="payload">
              {t('tasks.payload')}
            </TabsTrigger>
            <TabsTrigger value="body">
              {t('common.body')}
            </TabsTrigger>
            {task && (
              <TabsTrigger value="actions">
                {t('common.actions')}
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="details" className="mt-4">
            <DetailsConfigSection
              formData={formData}
              onChange={handleInputChange}
              fieldErrors={fieldErrors}
              allAccessGroups={allAccessGroups}
            />
          </TabsContent>

          <TabsContent value="data" className="mt-4">
            <DataSection
              formData={formData}
              onChange={handleInputChange}
              fieldErrors={fieldErrors}
            />
          </TabsContent>

          <TabsContent value="payload" className="mt-4">
            <PayloadSection
              formData={formData}
              onChange={handleInputChange}
              fieldErrors={fieldErrors}
            />
          </TabsContent>

          <TabsContent value="body" className="mt-4">
            <BodySection
              formData={formData}
              onChange={handleInputChange}
              isSaving={isSaving}
              fieldErrors={fieldErrors}
            />
          </TabsContent>

          {task && (
            <TabsContent value="actions" className="mt-4">
              <ActionsSection
                taskId={task.id}
                taskName={task.name}
                currentSharedDBName={task.sharedDBName}
                onDelete={handleDelete}
                isSaving={isSaving}
              />
            </TabsContent>
          )}
        </Tabs>
        )}
        {saveError && (
          <div className="p-3 bg-error/10 border border-error/20 rounded-md">
            <p className="text-sm text-error">{saveError}</p>
          </div>
        )}

        <div className="flex gap-3 pt-4 justify-end">
          <button
            onClick={onClose}
            disabled={isSaving}
            className="px-4 py-2 text-sm font-medium text-muted-foreground border border-border rounded-md hover:bg-secondary/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('common.cancel')}
          </button>
          <button
            data-testid="task-dialog-save"
            onClick={handleSave}
            disabled={isSaving}
            className="px-4 py-2 bg-accent text-white rounded-md text-sm font-medium hover:bg-accent/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSaving && (
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            )}
            {isSaving ? t('common.saving') : t('common.saveChanges')}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
